// Utilidades de PROYECTO compartidas (selector de cabecera + lista de proyectos).
// Un "proyecto" agrupa jobs por project_id (V3); cada versión es un job. El job que
// se abre es la versión COMPLETADA más alta (o el más reciente si ninguna completó).

// Limpieza ligera del file_name para mostrarlo como título: quita extensión,
// prefijo script_/guion_, y normaliza espacios. Mantiene sufijos como "(demo)".
export function cleanProjectTitle(fileName?: string | null): string {
  if (!fileName) return '';
  return fileName
    .replace(/\.(pdf|docx?|txt|fountain|fdx|rtf)$/i, '')
    .replace(/^(script|guion|gui[oó]n)[_\s-]+/i, '')
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface ProjectEntry {
  key: string;      // project_id (o file_name en jobs viejos sin project_id)
  jobId: string;    // job a abrir (versión completada más alta)
  name: string;     // título limpio para mostrar
  status: string;   // estado del job "mejor"
  isDemo: boolean;  // proyecto de demostración
}

type JobLike = {
  id: string;
  project_id?: string | null;
  version?: number | null;
  file_name: string;
  status: string;
  created_at: string;
  archived_at?: string | null;
};

const byVersionDesc = (a: JobLike, b: JobLike) =>
  ((b.version ?? 1) - (a.version ?? 1)) ||
  (new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

// Lista de proyectos ACTIVOS (no archivados) más recientes primero, para el selector.
export function recentProjects(jobs: JobLike[] | undefined | null, limit = 6): ProjectEntry[] {
  const map = new Map<string, JobLike[]>();
  for (const j of jobs ?? []) {
    if (j.archived_at) continue;
    // Agrupa por project_id (V3). Fallback a la PROPIA id del job (no a file_name):
    // agrupar por nombre de archivo fusionaría proyectos legacy distintos con el mismo
    // nombre → "cambiar de proyecto abre el equivocado", justo lo que esto arregla. En
    // la práctica la migración backfilleó project_id en todos, así que el fallback casi
    // nunca corre; pero así nunca fusiona por error.
    const key = j.project_id || j.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(j);
  }
  const entries = [...map.entries()].map(([key, list]) => {
    const completed = list.filter((j) => j.status === 'completed');
    const best = (completed.length ? completed : list).slice().sort(byVersionDesc)[0];
    return {
      key,
      jobId: best.id,
      name: cleanProjectTitle(best.file_name) || 'Sin título',
      status: best.status,
      // \bdemo\b casa "READTHEBONES (demo)" pero NO "mi_demo_reel.pdf" (falso positivo).
      isDemo: /\bdemo\b/i.test(best.file_name || ''),
      createdAt: best.created_at,
    };
  });
  entries.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return entries.slice(0, limit).map(({ createdAt: _c, ...e }) => e);
}
